import { useMemo } from 'react';

import { Practitioner, TaxRate } from '@dreryk/egabinet-private-medical-service-frontend-api';

import { FormContextValues, useFormController } from '@dreryk/react-common';

import { protectFeature } from '~libs.shared.system';

import { MedicalServiceDefaultPriceField } from '../shared/defaultPrice/MedicalServiceDefaultPriceField';
import { MedicalServiceFixedValueField } from '../shared/fixedValue/MedicalServiceFixedValueField';
import { MedicalServiceFormBean } from '../shared/form/medicalServiceForm.types';
import { MedicalServiceNameForInvoicingField } from '../shared/nameForInvoicing/MedicalServiceNameForInvoicingField';
import { MedicalServiceNameForInvoicingTextView } from '../shared/nameForInvoicing/MedicalServiceNameForInvoicingTextView';
import { MedicalServiceUsingNameAsNameForInvoicingField } from '../shared/nameForInvoicing/MedicalServiceUsingNameAsNameForInvoicingField';
import { nameForInvoicingPartVisible } from '../shared/nameForInvoicing/nameForInvocing.utils';
import { MedicalServiceNameForReceiptTextView } from '../shared/nameForReceipt/MedicalServiceNameForReceiptTextView';
import { MedicalServiceNameForReceiptField } from '../shared/nameForReceipt/fields/MedicalServiceNameForReceiptField';
import { MedicalServiceNameForReceiptTypeField } from '../shared/nameForReceipt/fields/MedicalServiceNameForReceiptTypeField';
import { nameForReceiptPartVisible } from '../shared/nameForReceipt/nameForReceipt.utils';
import { usePractitionersSynchronization } from '../shared/practitioner/practitioner.hooks';
import { MedicalServicePractitionerField } from '../shared/practitioner/single/MedicalServicePractitionerField';
import { MedicalServicePractitionerInteractiveTable } from '../shared/practitioner/table/MedicalServicePractitionerInteractiveTable';
import { MedicalServiceProportionalValueField } from '../shared/proportionalValue/MedicalServiceProportionalValueField';
import { MedicalServiceNameField } from '../shared/serviceName/MedicalServiceNameField';
import { useNotUniqueMedicalServiceNameErrorHandler } from '../shared/serviceName/serviceName.hooks';
import { MedicalServiceSettlementTypeField } from '../shared/settlement/MedicalServiceSettlementTypeField';
import { MedicalServiceSettlementTypeVisitor } from '../shared/settlement/settlement.types';
import { createMedicalServiceSettlementTypeVisitor } from '../shared/settlement/settlement.utils';
import { MedicalServiceTaxRateField } from '../shared/taxRate/MedicalServiceTaxRateField';
import { getDefaultTaxRate } from '../shared/taxRate/taxRate.utils';

import { StyledForColumnsSpan, StyledForm } from './MedicalServiceCreateForm.styled';

export type MedicalServiceCreateFormBean = MedicalServiceFormBean;

export type MedicalServiceCreateFormController = FormContextValues<MedicalServiceCreateFormBean>;

declare type MedicalServiceCreateFormRendererProps = Pick<
   MedicalServiceCreateFormController,
   'handleSubmit' | 'formState' | 'reset'
> & {
   setNotUniqueMedicalServiceNameError: VoidFunction;
};

declare type MedicalServiceCreateFormRenderer = (renderProps: MedicalServiceCreateFormRendererProps) => JSX.Element;

type MedicalServiceCreateFormProps = {
   readonly children: MedicalServiceCreateFormRenderer;
   readonly practitioners: Practitioner[];
   readonly taxRates: TaxRate[];
   readonly fiscalPrintEnabled: boolean;
};

// eslint-disable-next-line max-lines-per-function
export const MedicalServiceCreateForm = (props: MedicalServiceCreateFormProps): JSX.Element => {
   const invoiceCreateAvailable = protectFeature('creatingInvoices');
   const controller = useFormController<MedicalServiceCreateFormBean>(
      useMemo(
         () => ({
            name: null,
            usingNameAsNameForInvoicing: true,
            defaultPrice: null,
            taxRate: getDefaultTaxRate(props.taxRates)!,
            nameForReceiptType: 'SAME_AS_STANDARD',
            settlementType: 'None',
         }),
         [props.taxRates]
      )
   );

   const { settlementType, defaultPrice } = controller.watch(['settlementType', 'defaultPrice']);

   const medicalServiceSettlementTypeVisitor = useMemo<MedicalServiceSettlementTypeVisitor>(
      () => createMedicalServiceSettlementTypeVisitor(settlementType),
      [settlementType]
   );

   const setNotUniqueMedicalServiceNameError = useNotUniqueMedicalServiceNameErrorHandler(controller.setError);
   usePractitionersSynchronization({
      control: controller.control,
      getValue: controller.getValue,
      setValue: controller.setValue,
      trigger: controller.trigger,
      watch: controller.watch,
   });

   return (
      <>
         <StyledForm noValidate autoComplete={'off'}>
            <StyledForColumnsSpan>
               <MedicalServiceNameField name={'name'} register={controller.register} required />
            </StyledForColumnsSpan>

            {nameForInvoicingPartVisible(controller, invoiceCreateAvailable) && (
               <StyledForColumnsSpan>
                  <MedicalServiceNameForInvoicingTextView watch={controller.watch}>
                     <MedicalServiceUsingNameAsNameForInvoicingField
                        name={'usingNameAsNameForInvoicing'}
                        register={controller.register}
                        trigger={controller.trigger}
                     />
                     <MedicalServiceNameForInvoicingField
                        name={'nameForInvoicing'}
                        defaultValue={null}
                        register={controller.register}
                        setValue={controller.setValue}
                        trigger={controller.trigger}
                        watch={controller.watch}
                     />
                  </MedicalServiceNameForInvoicingTextView>
               </StyledForColumnsSpan>
            )}

            {nameForReceiptPartVisible(controller, props.fiscalPrintEnabled) && (
               <StyledForColumnsSpan>
                  <MedicalServiceNameForReceiptTextView watch={controller.watch}>
                     <MedicalServiceNameForReceiptTypeField
                        name={'nameForReceiptType'}
                        register={controller.register}
                     />
                     <MedicalServiceNameForReceiptField
                        name={'nameForReceipt'}
                        register={controller.register}
                        setValue={controller.setValue}
                        watch={controller.watch}
                     />
                  </MedicalServiceNameForReceiptTextView>
               </StyledForColumnsSpan>
            )}

            <MedicalServiceDefaultPriceField name={'defaultPrice'} register={controller.register} required />
            <MedicalServiceTaxRateField
               name={'taxRate'}
               register={controller.register}
               taxRates={props.taxRates}
               required
            />

            <MedicalServiceSettlementTypeField name={'settlementType'} register={controller.register} required />
            {medicalServiceSettlementTypeVisitor({
               visitNone: () => null,

               visitFixed: () => (
                  <MedicalServiceFixedValueField
                     name={'fixedValue'}
                     register={controller.register}
                     defaultPrice={defaultPrice}
                     trigger={controller.trigger}
                     required
                  />
               ),

               visitProportional: () => (
                  <MedicalServiceProportionalValueField
                     name={'proportionalValue'}
                     register={controller.register}
                     required
                  />
               ),
            })}

            <StyledForColumnsSpan>
               {props.practitioners.length === 1 ? (
                  <MedicalServicePractitionerField
                     register={controller.register}
                     control={controller.control}
                     practitioners={props.practitioners}
                  />
               ) : (
                  <MedicalServicePractitionerInteractiveTable
                     register={controller.register}
                     control={controller.control}
                     practitioners={props.practitioners}
                  />
               )}
            </StyledForColumnsSpan>
         </StyledForm>

         {props.children({
            handleSubmit: controller.handleSubmit,
            formState: controller.formState,
            reset: controller.reset,
            setNotUniqueMedicalServiceNameError: setNotUniqueMedicalServiceNameError,
         })}
      </>
   );
};
